import { SERVER } from './ApiUrls'

// SMS API service functions
export interface SMSMessage {
  id: string
  lead_phone: string
  lead_name: string
  direction: 'inbound' | 'outbound'
  body: string
  status: string
  sent_by_name?: string
  created_at: string
}

export interface SMSConversationResponse {
  error: boolean
  lead_id: string
  lead_name: string
  lead_phone: string
  count: number
  messages: SMSMessage[]
}

export const SMS_TRIGGER_MANUAL = "manual"
export const SMS_SENT_LEAD_ARCHIVED = "SMS sent. Lead archived."

export interface SmsTemplateOption {
  id: string
  key: string
  label: string
  description: string
  requires_attorney: boolean
  placeholders: string[]
  sort_order: number
}

export interface SendSMSRequest {
  lead_id: string
  message_body?: string
  template_key?: string
  attorney_id?: string
  trigger?: string
}

export interface SendSMSResponse {
  error: boolean
  message: string
  archived?: boolean
  data?: {
    id: string
    message_sid: string
    lead_phone: string
    direction: string
    body: string
    status: string
    created_at: string
  }
}

function smsUrl(path: string): string {
  const base = SERVER.endsWith('/') ? SERVER : `${SERVER}/`
  return `${base}sms/${path}`
}

function authHeaders(): Record<string, string> {
  return {
    'Authorization': `${localStorage.getItem('AccessToken')}`,
    'Content-Type': 'application/json',
  }
}

async function readJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({} as { message?: string }))
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
  }
  return response.json()
}

export const smsApi = {
  // Get SMS conversation for a lead
  async getConversation(leadId: string): Promise<SMSConversationResponse> {
    const response = await fetch(smsUrl(`conversation/lead/${leadId}/`), {
      headers: {
        'Authorization': `${localStorage.getItem('AccessToken')}`,
        'Content-Type': 'application/json',
      },
    })
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    return await response.json()
  },

  async getTemplates(): Promise<{ error: boolean; templates: SmsTemplateOption[] }> {
    const response = await fetch(smsUrl('templates/'), {
      headers: authHeaders(),
    })
    return readJson(response)
  },

  async renderTemplate(data: {
    lead_id: string
    template_key: string
    attorney_id?: string
  }): Promise<{ error: boolean; body: string; template_key: string; template_id: string }> {
    const response = await fetch(smsUrl('templates/render/'), {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(data),
    })
    return readJson(response)
  },

  async getReviewUrl(): Promise<{ error: boolean; review_url: string }> {
    const response = await fetch(smsUrl('settings/review-url/'), {
      headers: authHeaders(),
    })
    return readJson(response)
  },

  async updateReviewUrl(reviewUrl: string): Promise<{ error: boolean; review_url: string }> {
    const response = await fetch(smsUrl('settings/review-url/'), {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify({ review_url: reviewUrl }),
    })
    return readJson(response)
  },

  // Send SMS to a lead
  async sendSms(data: SendSMSRequest): Promise<SendSMSResponse> {
    const response = await fetch(smsUrl('send/'), {
      method: 'POST',
      headers: {
        'Authorization': `${localStorage.getItem('AccessToken')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
    }

    return await response.json()
  },

  // Get SMS conversation by phone number
  async getConversationByPhone(phoneNumber: string): Promise<SMSConversationResponse> {
    const response = await fetch(smsUrl(`conversation/phone/${phoneNumber}/`), {
      headers: {
        'Authorization': `${localStorage.getItem('AccessToken')}`,
        'Content-Type': 'application/json',
      },
    })
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    return await response.json()
  },

  // Get unread SMS counts per lead
  async getUnreadCounts(): Promise<{
    error: boolean
    total_unread: number
    leads: Array<{ lead_id: string; unread_count: number }>
  }> {
    const response = await fetch(smsUrl('unread-count/'), {
      headers: {
        'Authorization': `${localStorage.getItem('AccessToken')}`,
        'Content-Type': 'application/json',
      },
    })
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    return await response.json()
  },

  // Mark messages as read for a specific lead
  async markMessagesRead(leadId: string): Promise<{
    error: boolean
    message: string
    marked_read: number
  }> {
    const response = await fetch(smsUrl(`mark-read/${leadId}/`), {
      method: 'POST',
      headers: {
        'Authorization': `${localStorage.getItem('AccessToken')}`,
        'Content-Type': 'application/json',
      },
    })
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    return await response.json()
  }
}